import React, { useEffect, useMemo, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import logo from "../logo.PNG";
import { getValuesMetrics } from "../../util/metricsAPI";
import { exportGaitReportPdf } from "../../util/gaitReportPdf";
import {
  normalizeAnglesLikeSeries,
  shouldLogAngleSeriesInstrumentation,
} from "../../util/metricSeriesNormalize";

const ANGLE_METRICS = {
  hip: { left: "hip_left_flex", right: "hip_right_flex", label: "Hip flexion" },
  knee: { left: "knee_left_flex", right: "knee_right_flex", label: "Knee flexion" },
  pelvis: {
    left: "pelvic_tilt_left",
    right: "pelvic_tilt_right",
    label: "Pelvic tilt",
  },
};

const GRAPH_PRESETS = [
  { id: "hip", label: "Hip flexion", jointKey: "hip" },
  { id: "knee", label: "Knee flexion", jointKey: "knee" },
  { id: "pelvis", label: "Pelvic tilt", jointKey: "pelvis" },
];

function computeSummary(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }
  const ys = values.map((v) => v?.[0]).filter((v) => typeof v === "number");
  if (ys.length === 0) return null;
  const sum = ys.reduce((acc, v) => acc + v, 0);
  const mean = sum / ys.length;
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  return { mean, min, max };
}

function formatAngle(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(1)}°`;
}

function formatPercent(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(1)}%`;
}

function GaitReportModalContent({ trial, patientInfo, trialInfo, durationSec }) {
  const [tab, setTab] = useState("clinician");
  const [loading, setLoading] = useState(true);
  const [angleSummaries, setAngleSummaries] = useState({});
   // Raw angle series keyed by metric name
  const [angleSeries, setAngleSeries] = useState({});
  /** Max duration_sec reported by get-values-metrics (angles), when backend sends it */
  const [anglesApiDurationSec, setAnglesApiDurationSec] = useState(null);

  // Section visibility configuration
  const [clinicianSections, setClinicianSections] = useState({
    temporal: true,
    angleSummary: true,
    graphs: true,
    findings: true,
  });

  const [patientSections, setPatientSections] = useState({
    tiles: true,
    graphs: true,
    howToRead: true,
  });

  // Graph visibility + ordering per tab
  const [clinicianGraphs, setClinicianGraphs] = useState(
    GRAPH_PRESETS.map((g, idx) => ({ ...g, visible: true, order: idx }))
  );
  const [patientGraphs, setPatientGraphs] = useState(
    GRAPH_PRESETS.filter((g) => g.id !== "pelvis").map((g, idx) => ({
      ...g,
      visible: idx === 0, // default: only hip visible
      order: idx,
    }))
  );

  const clinicianRef = useRef(null);
  const patientRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setAnglesApiDurationSec(null);
      try {
        const entries = await Promise.all(
          Object.entries(ANGLE_METRICS).flatMap(([key, cfg]) => {
            return ["left", "right"].map(async (sideKey) => {
              const name = cfg[sideKey];
              if (!name) return [key, sideKey, null, null];
              const res = await getValuesMetrics(trial, "angles", name);
              const normalized = res
                ? normalizeAnglesLikeSeries(res, {
                    trialId: trial,
                    metricName: name,
                    logDebug: shouldLogAngleSeriesInstrumentation(trial),
                  })
                : null;
              const summary =
                normalized && Array.isArray(normalized.values)
                  ? computeSummary(normalized.values)
                  : null;
              const series =
                normalized && Array.isArray(normalized.values)
                  ? normalized.values
                  : null;
              const dur =
                normalized &&
                typeof normalized.duration_sec === "number" &&
                !Number.isNaN(normalized.duration_sec) &&
                normalized.duration_sec > 0
                  ? normalized.duration_sec
                  : null;
              return [key, sideKey, summary, { name, series, duration_sec: dur }];
            });
          })
        );

        if (cancelled) return;
        const next = {};
        const seriesMap = {};
        let maxAngleDur = null;
        entries.forEach(([jointKey, sideKey, summary, seriesInfo]) => {
          if (!next[jointKey]) next[jointKey] = {};
          next[jointKey][sideKey] = summary;
          if (seriesInfo && seriesInfo.name && seriesInfo.series) {
            seriesMap[seriesInfo.name] = seriesInfo.series;
          }
          if (seriesInfo && seriesInfo.duration_sec != null) {
            maxAngleDur =
              maxAngleDur == null
                ? seriesInfo.duration_sec
                : Math.max(maxAngleDur, seriesInfo.duration_sec);
          }
        });
        setAngleSummaries(next);
        setAngleSeries(seriesMap);
        setAnglesApiDurationSec(maxAngleDur);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (trial) {
      load();
    }
    return () => {
      cancelled = true;
    };
  }, [trial]);

  const patientName = patientInfo?.patient_name || "—";
  const trialId = trialInfo?.id ?? trial ?? "—";
  const trialDate = trialInfo?.name || trialInfo?.date || "—";

  const totalDuration = (() => {
    const videoDur =
      typeof durationSec === "number" && !Number.isNaN(durationSec) && durationSec > 0
        ? durationSec
        : null;
    const apiDur =
      typeof anglesApiDurationSec === "number" &&
      !Number.isNaN(anglesApiDurationSec) &&
      anglesApiDurationSec > 0
        ? anglesApiDurationSec
        : null;
    if (apiDur != null && videoDur != null) return Math.max(apiDur, videoDur);
    return apiDur ?? videoDur;
  })();

  const buildChartData = useMemo(() => {
    return (jointKey) => {
      const config = ANGLE_METRICS[jointKey];
      if (!config) return [];
      const leftSeries = angleSeries[config.left] || [];
      const rightSeries = angleSeries[config.right] || [];
      const maxLen = Math.max(leftSeries.length, rightSeries.length);
      if (maxLen === 0) return [];
      const denom = totalDuration ? maxLen / totalDuration : 1;
      const data = [];
      for (let i = 0; i < maxLen; i++) {
        const t = denom ? i / denom : i;
        const leftVal = leftSeries[i]?.[0];
        const rightVal = rightSeries[i]?.[0];
        data.push({
          t,
          left: typeof leftVal === "number" ? leftVal : null,
          right: typeof rightVal === "number" ? rightVal : null,
        });
      }
      return data;
    };
  }, [angleSeries, totalDuration, anglesApiDurationSec]);

  const handleGraphToggle = (tabKey, graphId) => {
    if (tabKey === "clinician") {
      setClinicianGraphs((prev) =>
        prev.map((g) =>
          g.id === graphId ? { ...g, visible: !g.visible } : g
        )
      );
    } else {
      setPatientGraphs((prev) =>
        prev.map((g) =>
          g.id === graphId ? { ...g, visible: !g.visible } : g
        )
      );
    }
  };

  const handleSectionToggle = (tabKey, sectionKey) => {
    if (tabKey === "clinician") {
      setClinicianSections((prev) => ({
        ...prev,
        [sectionKey]: !prev[sectionKey],
      }));
    } else {
      setPatientSections((prev) => ({
        ...prev,
        [sectionKey]: !prev[sectionKey],
      }));
    }
  };

  const handleDragEnd = (result, tabKey) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (tabKey === "clinician") {
      const sorted = [...clinicianGraphs].sort((a, b) => a.order - b.order);
      const [moved] = sorted.splice(sourceIndex, 1);
      sorted.splice(destIndex, 0, moved);
      setClinicianGraphs(
        sorted.map((g, idx) => ({
          ...g,
          order: idx,
        }))
      );
    } else {
      const sorted = [...patientGraphs].sort((a, b) => a.order - b.order);
      const [moved] = sorted.splice(sourceIndex, 1);
      sorted.splice(destIndex, 0, moved);
      setPatientGraphs(
        sorted.map((g, idx) => ({
          ...g,
          order: idx,
        }))
      );
    }
  };

  const visibleClinicianGraphs = useMemo(
    () =>
      [...clinicianGraphs]
        .filter((g) => g.visible)
        .sort((a, b) => a.order - b.order),
    [clinicianGraphs]
  );

  const visiblePatientGraphs = useMemo(
    () =>
      [...patientGraphs]
        .filter((g) => g.visible)
        .sort((a, b) => a.order - b.order),
    [patientGraphs]
  );

  const handleExportClick = async () => {
    const target =
      tab === "clinician" ? clinicianRef.current : patientRef.current;
    if (!target || isExporting) return;
    setIsExporting(true);
    target.classList.add("gait-report--export");
    try {
      const filename =
        tab === "clinician"
          ? `gait-report-${trialId}.pdf`
          : `gait-summary-${trialId}.pdf`;
      await exportGaitReportPdf(target, { filename });
    } finally {
      target.classList.remove("gait-report--export");
      setIsExporting(false);
    }
  };

  const clinicianContent = (
    <Box className="gait-report gait-report--clinician" ref={clinicianRef}>
      <Box className="gait-report__header">
        <Box className="gait-report__header-left">
          <img src={logo} alt="Company logo" className="gait-report__logo" />
          <Box>
            <Typography variant="h6" className="gait-report__title">
              Gait Analysis Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Trial-based kinematic summary for clinical review.
            </Typography>
          </Box>
        </Box>
        <Box className="gait-report__header-meta">
          <Typography variant="body2">
            <strong>Patient:</strong> {patientName}
          </Typography>
          <Typography variant="body2">
            <strong>Trial ID:</strong> {trialId}
          </Typography>
          <Typography variant="body2">
            <strong>Date:</strong> {trialDate}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box className="gait-report-config">
        <Typography variant="subtitle2" className="gait-report-config__title">
          Report contents
        </Typography>
        <Box className="gait-report-config__sections">
          <FormControlLabel
            control={
              <Checkbox
                checked={clinicianSections.temporal}
                onChange={() => handleSectionToggle("clinician", "temporal")}
                size="small"
              />
            }
            label="Temporal–spatial table"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={clinicianSections.angleSummary}
                onChange={() =>
                  handleSectionToggle("clinician", "angleSummary")
                }
                size="small"
              />
            }
            label="Angle summary table"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={clinicianSections.graphs}
                onChange={() => handleSectionToggle("clinician", "graphs")}
                size="small"
              />
            }
            label="Angle graphs"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={clinicianSections.findings}
                onChange={() => handleSectionToggle("clinician", "findings")}
                size="small"
              />
            }
            label="Key findings text"
          />
        </Box>
        <Box className="gait-report-config__graphs">
          <Typography variant="caption" color="text.secondary">
            Select which graphs to include and drag to reorder:
          </Typography>
          <DragDropContext
            onDragEnd={(result) => handleDragEnd(result, "clinician")}
          >
            <Droppable droppableId="clinician-graphs">
              {(provided) => (
                <ul
                  className="gait-report-config__graph-list"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {[...clinicianGraphs]
                    .sort((a, b) => a.order - b.order)
                    .map((g, index) => (
                      <Draggable
                        key={g.id}
                        draggableId={`clinician-${g.id}`}
                        index={index}
                      >
                        {(dragProvided) => (
                          <li
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className="gait-report-config__graph-item"
                          >
                            <span
                              className="gait-report-config__drag-handle"
                              {...dragProvided.dragHandleProps}
                            >
                              ::
                            </span>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={g.visible}
                                  onChange={() =>
                                    handleGraphToggle("clinician", g.id)
                                  }
                                  size="small"
                                />
                              }
                              label={g.label}
                            />
                          </li>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Box>

      {clinicianSections.temporal && (
        <Box className="gait-report__section">
          <Typography
            variant="subtitle1"
            className="gait-report__section-title"
          >
            Temporal–spatial overview
          </Typography>
          <table className="gait-report__table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Left</th>
                <th>Right</th>
                <th>Asymmetry</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Trial duration</td>
                <td colSpan={3}>
                  {totalDuration ? `${totalDuration.toFixed(2)} s` : "—"}
                </td>
              </tr>
              <tr>
                <td>Walking speed</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Cadence</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
              </tr>
              <tr>
                <td>Stance / swing (%)</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
        </Box>
      )}

      {clinicianSections.angleSummary && (
        <Box className="gait-report__section">
          <Typography
            variant="subtitle1"
            className="gait-report__section-title"
          >
            Sagittal plane joint angles (summary)
          </Typography>
          <table className="gait-report__table gait-report__table--angles">
            <thead>
              <tr>
                <th>Joint / segment</th>
                <th>Left mean</th>
                <th>Right mean</th>
                <th>Asymmetry (abs diff)</th>
                <th>Left range</th>
                <th>Right range</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(ANGLE_METRICS).map(([key, cfg]) => {
                const left = angleSummaries[key]?.left || null;
                const right = angleSummaries[key]?.right || null;
                const asym =
                  left && right ? Math.abs(left.mean - right.mean) : null;
                return (
                  <tr key={key}>
                    <td>{cfg.label}</td>
                    <td>{left ? formatAngle(left.mean) : "—"}</td>
                    <td>{right ? formatAngle(right.mean) : "—"}</td>
                    <td>{asym != null ? formatAngle(asym) : "—"}</td>
                    <td>
                      {left
                        ? `${formatAngle(left.min)} – ${formatAngle(
                            left.max
                          )}`
                        : "—"}
                    </td>
                    <td>
                      {right
                        ? `${formatAngle(right.min)} – ${formatAngle(
                            right.max
                          )}`
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Typography variant="caption" color="text.secondary">
            Values are computed over the available trial samples and are not yet
            normalised to gait cycle or compared to normative reference data.
          </Typography>
        </Box>
      )}

      {clinicianSections.graphs && visibleClinicianGraphs.length > 0 && (
        <Box className="gait-report__section">
          <Typography
            variant="subtitle1"
            className="gait-report__section-title"
          >
            Angle graphs (time vs. angle)
          </Typography>
          <Box className="gait-report__graphs-grid">
            {visibleClinicianGraphs.map((g) => {
              const data = buildChartData(g.jointKey);
              return (
                <Box key={g.id} className="gait-report__graph-card">
                  <Typography
                    variant="subtitle2"
                    className="gait-report__graph-title"
                  >
                    {g.label}
                  </Typography>
                  {data.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No data available for this metric.
                    </Typography>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height={150}
                      className="gait-report__graph-chart"
                    >
                      <LineChart
                        data={data}
                        margin={{ top: 6, right: 12, bottom: 22, left: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="t"
                          type="number"
                          domain={["dataMin", "dataMax"]}
                          tickCount={6}
                          allowDecimals
                          tickFormatter={(v) =>
                            typeof v === "number" ? v.toFixed(1) : ""
                          }
                          label={{
                            value: "Time (s)",
                            position: "insideBottom",
                            offset: -2,
                          }}
                        />
                        <YAxis width={44} tickMargin={2} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="left"
                          name="Left"
                          stroke="#ef4444"
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="right"
                          name="Right"
                          stroke="#3b82f6"
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                  <Box className="gait-report__graph-legend">
                    <span className="gait-report__graph-legend-item gait-report__graph-legend-item--left">
                      ● Left
                    </span>
                    <span className="gait-report__graph-legend-item gait-report__graph-legend-item--right">
                      ● Right
                    </span>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {clinicianSections.findings && (
        <Box className="gait-report__section gait-report__section--findings">
          <Typography
            variant="subtitle1"
            className="gait-report__section-title"
          >
            Key findings (draft)
          </Typography>
          <ul className="gait-report__list">
            <li>
              Use this section to summarise hip, knee and pelvic deviations
              relevant to the patient&apos;s primary impairment.
            </li>
            <li>
              Angle means and ranges above can be compared qualitatively to lab
              reference curves until normative bands are integrated.
            </li>
          </ul>
        </Box>
      )}
    </Box>
  );

  const patientContent = (
    <Box className="gait-report gait-report--patient" ref={patientRef}>
      <Box className="gait-report__header">
        <Box className="gait-report__header-left">
          <img src={logo} alt="Company logo" className="gait-report__logo" />
          <Box>
            <Typography variant="h6" className="gait-report__title">
              Gait Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Plain-language overview for the patient.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box className="gait-report-config gait-report-config--patient">
        <Typography variant="subtitle2" className="gait-report-config__title">
          Summary contents
        </Typography>
        <Box className="gait-report-config__sections">
          <FormControlLabel
            control={
              <Checkbox
                checked={patientSections.tiles}
                onChange={() => handleSectionToggle("patient", "tiles")}
                size="small"
              />
            }
            label="Summary tiles"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={patientSections.graphs}
                onChange={() => handleSectionToggle("patient", "graphs")}
                size="small"
              />
            }
            label="Simple angle graphs"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={patientSections.howToRead}
                onChange={() => handleSectionToggle("patient", "howToRead")}
                size="small"
              />
            }
            label="How to read this report"
          />
        </Box>
        <Box className="gait-report-config__graphs">
          <Typography variant="caption" color="text.secondary">
            Select which joint graphs to show:
          </Typography>
          <ul className="gait-report-config__graph-list gait-report-config__graph-list--patient">
            {patientGraphs
              .sort((a, b) => a.order - b.order)
              .map((g) => (
                <li
                  key={g.id}
                  className="gait-report-config__graph-item gait-report-config__graph-item--patient"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={g.visible}
                        onChange={() => handleGraphToggle("patient", g.id)}
                        size="small"
                      />
                    }
                    label={g.label}
                  />
                </li>
              ))}
          </ul>
        </Box>
      </Box>

      {patientSections.tiles && (
        <Box className="gait-report__tiles">
          <Box className="gait-report__tile">
            <Typography variant="subtitle2" className="gait-report__tile-title">
              Overall pattern
            </Typography>
            <Typography variant="body2">
              This summary describes how your hips, knees and pelvis move while
              you walk during this trial. It is intended to be read together
              with your clinician.
            </Typography>
          </Box>
          <Box className="gait-report__tile">
            <Typography variant="subtitle2" className="gait-report__tile-title">
              Hips &amp; pelvis
            </Typography>
            <Typography variant="body2">
              Small left–right differences in hip bending or pelvic tilt are
              common. Larger differences may relate to strength, flexibility or
              pain and will be discussed with you in context.
            </Typography>
          </Box>
          <Box className="gait-report__tile">
            <Typography variant="subtitle2" className="gait-report__tile-title">
              Knees
            </Typography>
            <Typography variant="body2">
              Knee bending when the foot contacts the ground and during swing is
              important for shock absorption and foot clearance. Your individual
              values are summarised in the clinician report.
            </Typography>
          </Box>
        </Box>
      )}

      {patientSections.graphs && visiblePatientGraphs.length > 0 && (
        <Box className="gait-report__section">
          <Typography
            variant="subtitle1"
            className="gait-report__section-title"
          >
            How your joints move
          </Typography>
          <Box className="gait-report__graphs-grid gait-report__graphs-grid--patient">
            {visiblePatientGraphs.map((g) => {
              const data = buildChartData(g.jointKey);
              return (
                <Box key={g.id} className="gait-report__graph-card">
                  <Typography
                    variant="subtitle2"
                    className="gait-report__graph-title"
                  >
                    {g.label}
                  </Typography>
                  {data.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No data available for this metric.
                    </Typography>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height={140}
                      className="gait-report__graph-chart"
                    >
                      <LineChart
                        data={data}
                        margin={{ top: 6, right: 8, bottom: 18, left: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="t"
                          tickFormatter={() => ""}
                          label={{
                            value: "Start of step → End of step",
                            position: "insideBottom",
                            offset: -8,
                          }}
                        />
                        <YAxis hide />
                        <Line
                          type="monotone"
                          dataKey="left"
                          name="Left"
                          stroke="#ef4444"
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="right"
                          name="Right"
                          stroke="#3b82f6"
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                  <Box className="gait-report__graph-legend gait-report__graph-legend--patient">
                    <span className="gait-report__graph-legend-item gait-report__graph-legend-item--left">
                      ● Left
                    </span>
                    <span className="gait-report__graph-legend-item gait-report__graph-legend-item--right">
                      ● Right
                    </span>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {patientSections.howToRead && (
        <Box className="gait-report__section">
          <Typography
            variant="subtitle1"
            className="gait-report__section-title"
          >
            How to read this report
          </Typography>
          <Typography variant="body2">
            The numbers and graphs in the clinician version compare your
            movement to typical walking patterns. They are one part of the full
            assessment together with your history, examination and goals. Use
            this summary to track changes over time rather than to
            self-diagnose.
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box className="gait-report-modal">
      <Box className="gait-report-modal__header-row">
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="Gait report view selection"
          className="gait-report-modal__tabs"
        >
          <Tab value="clinician" label="Clinician report" />
          <Tab value="patient" label="Patient summary" />
        </Tabs>
        <Button
          size="small"
          variant="contained"
          onClick={handleExportClick}
          disabled={isExporting || loading}
          className="gait-report-modal__export-btn"
        >
          {isExporting ? "Generating PDF…" : "Download PDF"}
        </Button>
      </Box>

      <Box className="gait-report-modal__body">
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : tab === "clinician" ? (
          clinicianContent
        ) : (
          patientContent
        )}
      </Box>
    </Box>
  );
}

export default GaitReportModalContent;

